/* eslint-disable warp-drive/no-legacy-request-patterns */
import type Subject from '@linc-technologies/linced-common/models/goals/subject';
import type ReportTemplateSection from '@linc-technologies/linced-common/models/reports/report-template-section';
import type Rubric from '@linc-technologies/linced-common/models/reports/rubric';
import type RubricElement from '@linc-technologies/linced-common/models/reports/rubric-element';
import type RubricFamily from '@linc-technologies/linced-common/models/reports/rubric-family';
import type Store from '@linc-technologies/linced-common/services/store';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import type {
  Milestone,
  ProgressionPerson,
} from '@linc-technologies/linced-common/utils/typedefs';

// A stored strand value: a student report section's element progression.
interface ElementValue {
  elementId: string;
  judgement: string;
  effort: string;
}

/**
 * The default rubric family: the one report sections use when they haven't
 * chosen a family, and the fallback when the chosen family has no rubric for
 * a subject and milestone. Only the school's jurisdiction's families are
 * loaded, so there is at most one.
 */
export function defaultRubricFamily(store: Store): RubricFamily | null {
  return (
    store
      .peekAll<RubricFamily>('reports/rubric-family')
      .find((family) => family.isDefault) ?? null
  );
}

/**
 * The rubric family chosen on a report template section, if any. The
 * section's configuration is JSON, as a string or already parsed.
 */
export function sectionRubricFamilyId(
  templateSection:
    Pick<ReportTemplateSection, 'configuration'> | null | undefined,
): string | null {
  if (!templateSection) return null;
  let config = templateSection.configuration;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch {
      return null;
    }
  }
  return (config as { rubricFamilyId?: string } | null)?.rubricFamilyId || null;
}

// Rubrics saved before families existed have no familyId and belong to the
// default family.
export function rubricFamilyIdOf(
  rubric: Pick<Rubric, 'familyId'>,
  defaultFamilyId: string | null | undefined,
): string | null {
  return rubric.familyId || defaultFamilyId || null;
}

/**
 * Rubric families in display order: the default first, then by name.
 */
export function sortRubricFamilies(
  families: Iterable<RubricFamily> | null | undefined,
): RubricFamily[] {
  return [...(families ?? [])].sort((a, b) => {
    if (a.isDefault !== b.isDefault) {
      return a.isDefault ? -1 : 1;
    }
    return (a.name || '').localeCompare(b.name || '');
  });
}

/**
 * The family with the given id from a list of families, or the list's
 * default family when none is chosen (or the chosen one isn't in the list).
 */
export function rubricFamilyOrDefault(
  families: readonly RubricFamily[],
  familyId: string | null | undefined,
): RubricFamily | null {
  return (
    (familyId && families.find((family) => family.id === familyId)) ||
    families.find((family) => family.isDefault) ||
    null
  );
}

/**
 * Load the global, active rubrics (with their elements and criteria) for a
 * jurisdiction.
 */
export function loadRubrics(store: Store, jurisdiction: number) {
  return store.query<Rubric>('reports/rubric', {
    isGlobal: true,
    isActive: true,
    includeElementsAndCriteria: true,
    jurisdiction,
  });
}

/**
 * Resolve the rubric a student uses for a subject: the chosen family's
 * rubric for the subject and the student's current milestone, falling back
 * to the default family's. familyId is the section's chosen family (null to
 * use the default).
 */
export function resolveRubric(
  subject: Subject | null | undefined,
  student: ProgressionPerson | null | undefined,
  store: Store,
  rolloverMonthDate: string,
  familyId?: string | null,
): Rubric | null {
  if (!subject || !student) {
    return null;
  }

  const templateSubjectId = subject.templateId;
  if (!templateSubjectId) {
    return null;
  }

  const currentMilestone = getCurrentMilestone(
    // TS-NARROWED: a subject's milestones are saved records, so their ids
    // are set.
    (subject.milestones ?? []) as Milestone[],
    student,
    rolloverMonthDate,
  );
  const currentMilestoneTemplateId = currentMilestone?.templateId || null;

  const candidates = store.peekAll<Rubric>('reports/rubric').filter((r) => {
    if (r.next) return false;
    if (r.templateSubjectId !== templateSubjectId) return false;
    if (!currentMilestoneTemplateId) return true;
    return [...(r.templateMilestoneIds ?? [])].includes(
      currentMilestoneTemplateId,
    );
  });
  if (!candidates.length) {
    return null;
  }

  const defaultFamilyId = defaultRubricFamily(store)?.id ?? null;
  const inFamily = (id: string | null | undefined) =>
    candidates.find((r) => rubricFamilyIdOf(r, defaultFamilyId) === id);

  // Families not loaded yet (or none set up): every rubric counts as the
  // default family's, so keep showing one
  if (!defaultFamilyId) {
    return inFamily(familyId) ?? candidates[0] ?? null;
  }

  // Never fall back to another family's rubric: that would show strands
  // the section didn't choose
  return (
    inFamily(familyId || defaultFamilyId) ?? inFamily(defaultFamilyId) ?? null
  );
}

/**
 * The elements (strands) of a rubric, in display order.
 */
export function rubricElements(
  rubric: Rubric | null | undefined,
  store: Store,
): RubricElement[] {
  if (!rubric) {
    return [];
  }
  const rubricId = rubric.id;
  return store
    .peekAll<RubricElement>('reports/rubric-element')
    .filter((element) => element.belongsTo('rubricId').id() === rubricId)
    .sort(byKeys('sequence'));
}

/**
 * The elements (strands) of the rubric a student uses for a subject.
 */
export function resolveRubricElements(
  subject: Subject | null | undefined,
  student: ProgressionPerson | null | undefined,
  store: Store,
  rolloverMonthDate: string,
  familyId?: string | null,
): RubricElement[] {
  return rubricElements(
    resolveRubric(subject, student, store, rolloverMonthDate, familyId),
    store,
  );
}

/**
 * Sum a section's strand values (judgement or effort) for the overall
 * grade. Only values stored against the given rubric elements count (e.g.
 * not those stored before the template's rubric family changed); with no
 * elements every value counts. Empty and "na" values are skipped.
 */
export function rubricElementTotals(
  elementProgressions: Iterable<ElementValue>,
  elements: readonly Pick<RubricElement, 'id'>[] | null | undefined,
  field: 'judgement' | 'effort',
): { total: number; count: number } {
  const elementIds = new Set((elements ?? []).map((element) => element.id));
  let total = 0;
  let count = 0;
  for (const ep of elementProgressions) {
    if (elementIds.size && !elementIds.has(ep.elementId)) continue;
    const v = ep[field];
    if (v != null && v !== '' && v !== 'na') {
      total += parseInt(v, 10);
      count++;
    }
  }
  return { total, count };
}

/**
 * A section's stored strand values that belong to the student's current
 * rubric. Values stored against another rubric's elements (e.g. before the
 * template's rubric family changed) are left out, so a report never shows
 * strands from two rubrics. If the current rubric can't be resolved (e.g.
 * rubrics aren't loaded), every stored value is kept.
 */
export function currentRubricProgressions<T extends ElementValue>(
  elementProgressions: Iterable<T> | null | undefined,
  {
    store,
    subject,
    student,
    rolloverMonthDate,
    templateSection,
  }: {
    store: Store;
    subject: Subject | null | undefined;
    student: ProgressionPerson | null | undefined;
    rolloverMonthDate: string;
    templateSection?: Pick<ReportTemplateSection, 'configuration'> | null;
  },
): T[] {
  const entries = [...(elementProgressions ?? [])];
  const rubric = resolveRubric(
    subject,
    student,
    store,
    rolloverMonthDate,
    sectionRubricFamilyId(templateSection),
  );
  if (!rubric) {
    return entries;
  }
  const elementIds = new Set(
    rubricElements(rubric, store).map((element) => element.id),
  );
  return entries.filter((entry) => elementIds.has(entry.elementId));
}
